import { FC } from 'react'
import { useFormik } from 'formik'
import { enqueueSnackbar } from 'notistack'

import {
  ExternalTournament,
  ExternalTournamentWithEvents,
  GetSport,
  GetSports,
  TournamentEvent
} from '@img-arena/consoles-api-streaming'
import { ConsoleDialog } from '@/components/ConsoleDialog'
import { formatExternalEvent, prepareEventData } from '@/components/ImportEvents/utils'
import { SportPropertyAutocomplete } from '@/components/inputs/SportPropertyAutocomplete'
import { streamingRestApi } from '@/service/streamingApi'
import { FormDialogProps } from '@/types/forms'
import { formikSaveButtonProps } from '@/utils/forms'
import { onFieldChange } from '@/utils/forms/formik'

import { DIALOG_TITLE_LABEL } from './constants'

const importTournament = async (
  sportPropertyId: number,
  tournament: ExternalTournamentWithEvents
) => {
  const {
    data: { id: newExchangeTournamentId }
  } = await streamingRestApi.endpoints.importEvents.importTournamentInToProperty(
    sportPropertyId,
    tournament.id
  )
  tournament.exchangeTournamentId = newExchangeTournamentId

  enqueueSnackbar(`Successfully imported tournament: ${tournament.name}!`, { variant: 'success' })
}

export const importSingleEvent = async (
  tournament: ExternalTournamentWithEvents,
  event: TournamentEvent
) => {
  const selectedEvent = tournament.events.find((e) => e.id === event.id)

  if (!selectedEvent) return

  const exchangeEvent = prepareEventData(
    formatExternalEvent(selectedEvent, tournament.exchangeTournamentId)
  )

  if (tournament.exchangeTournamentId) {
    await streamingRestApi.endpoints.importEvents.importEvent(selectedEvent.id, tournament.id)
    await streamingRestApi.endpoints.importEvents.createEvent(
      tournament.exchangeTournamentId,
      exchangeEvent
    )
  }
  enqueueSnackbar(`Successfully imported event: ${exchangeEvent.title}!`, {
    variant: 'success'
  })
}

export const importAllEvents = async (tournament: ExternalTournamentWithEvents) => {
  const eventIds = tournament.events.map((event) => event.id)
  await streamingRestApi.endpoints.importEvents.importAllEventsInToProperty(tournament.id, eventIds)

  const exchangeEvents = tournament.events.map((event) =>
    prepareEventData(formatExternalEvent(event, tournament.exchangeTournamentId))
  )
  if (tournament.exchangeTournamentId) {
    await streamingRestApi.endpoints.importEvents.uploadEvents(
      tournament.exchangeTournamentId,
      exchangeEvents
    )
  }

  enqueueSnackbar('Successfully imported all events!', { variant: 'success' })
}

export type ImportEventsFormValues = {
  sportProperty: GetSport['properties'][number] | null
}

type ImportEventsFormProps = FormDialogProps<
  GetSports,
  { data?: TournamentEvent | ExternalTournament }
> & {
  tournament: ExternalTournamentWithEvents
  multiple?: boolean
}

export const ImportEventsForm: FC<ImportEventsFormProps> = ({
  onClose,
  onSubmit,
  open,
  tournament,
  multiple = false,
  data
}) => {
  const formik = useFormik<ImportEventsFormValues>({
    initialValues: { sportProperty: null },
    onSubmit: async ({ sportProperty }) => {
      if (!sportProperty) return

      try {
        if (!tournament.exchangeTournamentId) {
          await importTournament(sportProperty.id, tournament)
        }

        if (!multiple && !!data?.data) {
          await importSingleEvent(tournament, data.data as TournamentEvent)
        } else if (multiple && !data?.data) {
          await importAllEvents(tournament)
        }

        formik.resetForm()
        onSubmit?.()
        onClose?.()
      } catch (error) {
        enqueueSnackbar(String(error), { variant: 'error' })
        throw error
      }
    }
  })

  return (
    <ConsoleDialog
      open={!!open}
      title={DIALOG_TITLE_LABEL}
      description={multiple && tournament ? tournament.name : ''}
      onClose={() => {
        formik.resetForm()
        onClose?.()
      }}
      SaveButtonProps={{ ...formikSaveButtonProps(formik), text: 'Save' }}
    >
      <SportPropertyAutocomplete
        onChange={(value) =>
          onFieldChange(formik, 'sportProperty', { id: value?.id || 0, name: value?.label || '' })
        }
      />
    </ConsoleDialog>
  )
}
